import { useState, useEffect, useRef } from "react";
import '../App.css';
import UploadVideo from "../components/uploadOfflineVideo";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_MODEL = process.env.REACT_APP_DEFAULT_MODEL;

export default function MainFunctionsPage() {
  const { jobId } = useParams();
  const pollingRef = useRef();
  const [log, setLog] = useState(["Console output"]);
  const [submittedFile, setsubmittedFile] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [videoURLProcessed, setVideoURLProcessed] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [detections, setDetections] = useState([]);
  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [processedImages, setProcessedImages] = useState([]);
  const timerIdRef = useRef(null);
  const navigate = useNavigate();

  const fetchLabels = async (modelName) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/model_labels?model=${modelName}`);
      const fetchedLabels = Object.values(response.data);
      setLabels(fetchedLabels);

      setSelectedLabel(prev =>
        fetchedLabels.includes(prev) ? prev : (fetchedLabels[0] || "")
      );
    } catch (error) {
      setLog((prevLogs) => [...prevLogs, "Error fetching labels: " + error.message + " " + modelName]);
    }
  };

  const fetchJob = async (jobId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/status/${jobId}`);
      setLog([""])
      setLog((prevLogs) => [...prevLogs, `Job status: ${response.data.status}`]);
      if (!response.data.result) {
        return response;
      }
      setLog((prevLogs) => [...prevLogs, `File: ${response.data.result.filename}`]);
      setLog((prevLogs) => [...prevLogs, `Total frames: ${response.data.result.total_frames}`]);
      setDetections(response.data.result.message || []);
      setElapsedTime(Math.round(response.data.result.elapsed_seconds || 0));
      if (response.data.result.processed_video_url) {
        setVideoURLProcessed(`${BACKEND_URL}/${response.data.result.processed_video_url}`);
      } else {
        setProcessedImages(response.data.result.processed_images);
      }
      return response;
    } catch (error) {
      jobId = null; // Reset jobId if fetching fails
      return null
    }
  };

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/model_names`)
        setModels(Object.values(response.data));
      } catch (error) {
        setLog((prevLogs) => [...prevLogs, "Error fetching models: " + error.message]);
      }
    };

    const startTimers = () => {
      setElapsedTime(0);
      timerIdRef.current = setInterval(() => setElapsedTime((prev) => prev + 1), 1000);
    };

    const stopTimers = () => {
      clearInterval(timerIdRef.current);
    };

    pollingRef.current = true;

    const poll = async () => {
      const response = await fetchJob(jobId);
      if (
        !response || // Stop if fetchJob failed
        (response.data && (response.data.status === "done" || response.data.status === "error"))
      ) {
        pollingRef.current = false;
        stopTimers();
        console.log("Polling stopped");
        return;
      }
      console.log("Polling");
      if (pollingRef.current) {
      setTimeout(poll, 3000);
      }
    };

    if (jobId) {
      pollingRef.current = true;
      startTimers();
      poll();
    }

    fetchModels();
    fetchLabels(selectedModel);

    return () => {
      pollingRef.current = false;
      stopTimers();
    };
  }, [jobId]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setsubmittedFile(file);
      if (file.type.startsWith("video/")) {
      setVideoURL(URL.createObjectURL(file));
      }
    }
  };

  const handleLabelChange = (event) => {
    setSelectedLabel(event.target.value);
  }

  const handleModelChange = (event) => {
    const selectedModel = event.target.value;
    setSelectedModel(selectedModel);
    fetchLabels(selectedModel);
  };

  const handleUpload = async () => {
    setDetections([""]);
    if (!submittedFile) {
      setLog("")
      setLog((prevLogs) => [...prevLogs, "Please select a video file."]);
      return;
    }

    const formData = new FormData();
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    
    formData.append("file", submittedFile);
    formData.append("label", selectedLabel);
    formData.append("model", selectedModel);
    formData.append("user_id", userId);

    const startButton = document.getElementById("start-upload");
    startButton.disabled = true;
    startButton.style.cursor = "not-allowed";

    try {
      const response = await axios.post(`${BACKEND_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      /* Send user to the job url page */
      navigate(`/main/${response.data.job_id}`);

      startButton.disabled = false;
      startButton.style.cursor = "pointer";
    } catch (error) {
      setLog((prevLogs) => [...prevLogs, `Error uploading video. ${error.message}`]);
      startButton.disabled = false;
      startButton.style.cursor = "pointer";
    }
  };

  return (
    <div className="container">
      <span
        onClick={() => navigate("/")}
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          cursor: "pointer",
          fontSize: "24px",
        }}
      >
        &lt;
      </span>
      <div className="image-container">
        <div className="image-box">
          <UploadVideo videoURL={videoURL} />
        </div>
        <div className="image-box">
            {videoURLProcessed ? (
              <UploadVideo videoURL={videoURLProcessed} submittedFile={submittedFile} />
            ) : processedImages.length > 0 ? (
              <div className="processed-images-gallery">
                {processedImages.map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={`${BACKEND_URL}${imgUrl}`}
                    alt={`Processed ${idx}`}  
                    style={{ maxWidth: "90%", margin: "10px" }}
                  />
                ))}
              </div>
            ) : null}
        </div>
      </div>

      <div className="bottom-section">
        <div className="box">
          <h2>Configuration</h2>
          <h4>Label selection</h4>
          <select className="select" onChange={handleLabelChange}>
            {labels.map((label, index) => (
              <option key={index} value={label}>
                {label}
              </option>
            ))}
          </select>
          <h4>Offline video</h4>
          <input type="file" accept="video/mp4,video/mov,application/zip" onChange={handleFileChange} />
          <h4>Select model</h4>
          <select className="select" onChange={handleModelChange} value={selectedModel}>
            {models.map((model, index) => (
              <option key={index} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>

        <div className="box">
          <h2>Start tracking</h2>
          <button id="start-upload" onClick={handleUpload}>Start</button>
          <p>{Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, "0")}</p>
        </div>

        <div className="box">
          <h2>Console</h2>
          <div className="console">
            {log.map((entry, index) => (
              <span key={index} className="log-entry">
                {entry}
              </span>
            ))}
            {detections.map((entry, index) => (
              <span key={`detection-${index}`} className="log-entry">
                {entry}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}